import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { PERMISSIONS } from "@/constants/permissions"
import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { useMemo } from "react"
import { useNavigate } from "react-router"
import {
  type ConformityListItem,
  type ConformityStatus,
  useConformityList,
} from "../../api/conformityApi"
import {
  type QuestionnaireRow,
  type QuestionnaireStatus,
  useQuestionnaires,
} from "../../api/customerRequestsApi"
import { intlLocale } from "../../utils/date"
import { regulationLabel, regulationLabels } from "../../utils/regulationLabel"
import {
  canonicalStepForStatus,
  isFailedStatus,
  isInFlightStatus,
} from "../customer-requests/processing/processingSteps"
import {
  conformityCreatePath,
  customerRequestConfirmPath,
  customerRequestDetailPath,
  customerRequestProcessPath,
} from "../customer-requests/routes"
import { McPill } from "./McPill"

// Requests still in intake — "received or ongoing" before the answering
// pipeline even starts. `processed` moves on to processingStatus below;
// `extraction_failed` is terminal with no action here.
const ACTIVE_REQUEST_STATUSES: ReadonlySet<QuestionnaireStatus> = new Set([
  "stored_pending",
  "extracting",
  "needs_confirm",
])

const CANONICAL_STEP_LABEL_KEYS = {
  mapping: "MC_QP_STEP_MAPPING_TITLE",
  reconcile: "MC_QP_STEP_RECONCILE_TITLE",
  gaps: "MC_QP_STEP_GAPS_TITLE",
  review: "MC_QP_STEP_REVIEW_TITLE",
} as const

// True once a request has entered the answering pipeline (processingStatus
// set) and hasn't reached a terminal state — `sent` is done, `failed_*` has
// no review action to offer here (mirrors ACTIVE_LIFECYCLE_STATUSES's same
// call for supplier declarations). Most requests reach intake `status:
// processed` quickly and then spend the rest of their life in this pipeline
// — filtering on `status` alone (as this used to) misses all of that.
function isMidPipeline(row: QuestionnaireRow): boolean {
  const ps = row.processingStatus
  return ps !== null && ps !== "sent" && !isFailedStatus(ps)
}

function isActiveRequest(row: QuestionnaireRow): boolean {
  return ACTIVE_REQUEST_STATUSES.has(row.status) || isMidPipeline(row)
}

// Declarations not yet sent to the customer — "in flight". `sent` is settled.
const ACTIVE_DECLARATION_STATUSES: ReadonlySet<ConformityStatus> = new Set([
  "draft",
  "approved",
  "downloaded",
])

type Translate = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

function requestStatusPill(
  row: QuestionnaireRow,
  translate: Translate,
): { label: string; tone: "muted" | "info" | "attention" } {
  // Mid-pipeline rows (see isMidPipeline) show which of the 4 answering
  // steps they're at instead of the intake status — the intake status is
  // already "processed" for all of these, which tells the user nothing.
  if (isMidPipeline(row)) {
    const step = canonicalStepForStatus(row.processingStatus)
    return {
      label: translate(CANONICAL_STEP_LABEL_KEYS[step]),
      tone: isInFlightStatus(row.processingStatus) ? "info" : "attention",
    }
  }
  switch (row.status) {
    case "stored_pending":
      return { label: translate("MC_MD_WORK_STATUS_RECEIVED"), tone: "muted" }
    case "extracting":
      return {
        label: translate("MC_MD_WORK_STATUS_PROCESSING"),
        tone: "info",
      }
    case "needs_confirm":
      return {
        label: translate("MC_MD_WORK_STATUS_NEEDS_REVIEW"),
        tone: "attention",
      }
    // "processed" without a processingStatus (isMidPipeline above already
    // handles the normal case) and "extraction_failed" — not in
    // ACTIVE_REQUEST_STATUSES so this row shouldn't normally reach here, but
    // the switch stays exhaustive over QuestionnaireStatus rather than
    // leaking the raw value.
    case "processed":
      return { label: translate("MC_Q_STATUS_PROCESSED"), tone: "muted" }
    case "extraction_failed":
      return {
        label: translate("MC_Q_STATUS_EXTRACTION_FAILED"),
        tone: "attention",
      }
    default:
      return { label: translate("MC_MD_WORK_STATUS_OTHER"), tone: "muted" }
  }
}

function declarationStatusPill(
  status: ConformityStatus,
  translate: Translate,
): { label: string; tone: "muted" | "info" | "attention" } {
  switch (status) {
    case "approved":
      return { label: translate("MC_CONF_STATUS_APPROVED"), tone: "info" }
    case "downloaded":
      return { label: translate("MC_CONF_STATUS_DOWNLOADED"), tone: "muted" }
    default:
      return { label: translate("MC_CONF_STATUS_DRAFT"), tone: "muted" }
  }
}

function requestPath(row: QuestionnaireRow): string {
  // needs_confirm/extracting/extraction_failed all belong to the intake step
  // — mirrors CustomerRequestsModule's onOpen routing.
  const isIntakeStep =
    row.status === "needs_confirm" ||
    row.status === "extracting" ||
    row.status === "extraction_failed"
  if (isIntakeStep) return customerRequestConfirmPath(row.id)
  return row.processingStatus !== null
    ? customerRequestProcessPath(row.id)
    : customerRequestDetailPath(row.id)
}

function formatDate(
  value: string | Date | null | undefined,
  locale: string,
): string | null {
  if (!value) return null
  const d = typeof value === "string" ? new Date(value) : value
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(locale)
}

function ActionCard({
  title,
  meta,
  tone,
  statusLabel,
  primaryLabel,
  onPrimary,
}: {
  title: string
  meta: string
  tone: "muted" | "info" | "attention" | "danger" | "success"
  statusLabel: string
  primaryLabel: string
  onPrimary: () => void
}) {
  return (
    <div className="rounded-lg border border-grey4 bg-grey2 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">
            {title}
          </div>
          <div className="mt-0.5 text-xs text-grey5">{meta}</div>
        </div>
        <McPill tone={tone}>{statusLabel}</McPill>
      </div>
      <Button size="sm" className="mt-3 gap-1.5 rounded-md" onClick={onPrimary}>
        {primaryLabel}
        <ArrowRight size={14} />
      </Button>
    </div>
  )
}

function requestCard(
  row: QuestionnaireRow,
  navigate: ReturnType<typeof useNavigate>,
  translate: Translate,
) {
  const pill = requestStatusPill(row, translate)
  const metaParts = [
    row.due && translate("MC_MD_WORK_META_DUE", { date: row.due }),
    row.frameworks.length > 0
      ? regulationLabels(row.frameworkCodes, row.frameworks, translate).join(
          ", ",
        )
      : null,
  ].filter(Boolean)

  return (
    <ActionCard
      key={row.id}
      title={row.requestId}
      meta={metaParts.join(" · ") || "—"}
      tone={pill.tone}
      statusLabel={pill.label}
      primaryLabel={translate("MC_MD_WORK_ACTION_REVIEW_REQUEST")}
      onPrimary={() => navigate(requestPath(row))}
    />
  )
}

function declarationCard(
  row: ConformityListItem,
  navigate: ReturnType<typeof useNavigate>,
  translate: Translate,
  locale: string,
) {
  const pill = declarationStatusPill(row.status, translate)
  const generatedDate = formatDate(row.generatedAt, locale)
  const metaParts = [
    generatedDate &&
      translate("MC_MD_WORK_META_GENERATED", { date: generatedDate }),
    row.framework
      ? regulationLabel(row.frameworkCode, row.framework, translate)
      : row.frameworkKeys.join(", "),
  ].filter(Boolean)

  return (
    <ActionCard
      key={row.id}
      title={row.reference ?? translate("MC_MD_WORK_DECLARATION")}
      meta={metaParts.join(" · ") || "—"}
      tone={pill.tone}
      statusLabel={pill.label}
      primaryLabel={translate(
        row.needsReview
          ? "MC_MD_WORK_ACTION_REVIEW_DECLARATION"
          : "MC_MD_WORK_ACTION_VIEW_DECLARATION",
      )}
      onPrimary={() =>
        navigate(
          `/material-compliance/customer-studio/conformity/edit/${row.id}`,
        )
      }
    />
  )
}

interface CustomerActiveWorkSectionProps {
  /** Whether entityId is a customer's own id or a product's id — scopes both
   * the customer requests and the confirmatory declarations queries. */
  scope: "customer" | "product"
  entityId: string
}

/** Product / Customer panel equivalent of SupplierActiveWorkSection — shows
 * this entity's active customer requests (inbound questionnaires) and
 * confirmatory declarations (outbound conformity), with the same
 * generate/open-studio footer as the previous static placeholder. */
export function CustomerActiveWorkSection({
  scope,
  entityId,
}: CustomerActiveWorkSectionProps) {
  const navigate = useNavigate()
  const { translate, currentLanguage } = useTranslation()
  const locale = intlLocale(currentLanguage)

  const { data: requestData, isLoading: requestsLoading } = useQuestionnaires(
    scope === "customer"
      ? { customerId: entityId, pageSize: 20 }
      : { productId: entityId, pageSize: 20 },
  )
  const requests = useMemo(
    () => (requestData?.items ?? []).filter(isActiveRequest),
    [requestData],
  )

  const { data: conformityData, isLoading: conformityLoading } =
    useConformityList(
      scope === "customer"
        ? { customerId: entityId, pageSize: 20 }
        : { productId: entityId, pageSize: 20 },
    )
  const declarations = useMemo(
    () =>
      (conformityData?.items ?? []).filter((d) =>
        ACTIVE_DECLARATION_STATUSES.has(d.status),
      ),
    [conformityData],
  )

  const isLoading = requestsLoading || conformityLoading

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 px-4 pb-4 pt-3">
        <Skeleton className="h-[76px] w-full rounded-lg" />
        <Skeleton className="h-[76px] w-full rounded-lg" />
      </div>
    )
  }

  const hasAny = requests.length > 0 || declarations.length > 0

  return (
    <div className="flex flex-col gap-3 px-4 pb-4 pt-3">
      {hasAny && (
        <div className="flex max-h-96 flex-col gap-2 overflow-y-auto">
          {requests.map((row) => requestCard(row, navigate, translate))}
          {declarations.map((row) =>
            declarationCard(row, navigate, translate, locale),
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <PermissionGate
          permission={PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE}
        >
          <Button
            size="sm"
            variant={hasAny ? "outline" : "default"}
            className="gap-1.5 rounded-md"
            onClick={() =>
              navigate(conformityCreatePath({ scope, ids: [entityId] }))
            }
          >
            {translate("MC_CONFORMITY_BTN_GENERATE")}
            <ArrowRight size={14} />
          </Button>
        </PermissionGate>
        <PermissionGate permission={PERMISSIONS.MC_QUESTIONNAIRES_VIEW}>
          <button
            type="button"
            onClick={() =>
              navigate("/material-compliance/customer-studio/dashboard")
            }
            className="flex items-center gap-1 text-xs font-medium text-yellow1 hover:underline"
          >
            {translate("MC_MD_WORK_OPEN_CUSTOMER_STUDIO")}
            <ArrowUpRight size={12} />
          </button>
        </PermissionGate>
      </div>
    </div>
  )
}
